import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { CalendarDate, getLocalTimeZone, startOfMonth, today } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import { EMPTY_BILLING_CARDS, fetchBillingSummary, fetchMappableInvoices, fetchRefreshStatus, generateInvoice, refreshInvoices, saveAdjustedAmounts, saveCommissionAmount, saveDevelopmentHours, saveInvoiceMappings, selectBillingSummary, selectBillingSummaryError, selectBillingSummaryLoading, selectGeneratingInvoice, selectSavingAdjustedAmounts, selectSavingCommissionAmount, selectSavingDevelopmentHours, selectSavingInvoiceMappings, } from "../../redux/billingSummarySlice";
import { fetchDesignationWiseUsers, selectDesignationWiseUsers } from "../../redux/clientDataSlice";
import { selectBillingHasFullAccess, selectToken } from "../../redux/authSlice";
import { ApiCall } from "../../helper/axios";
import { AM_DESIGNATIONS, collectUsersByDesignations } from "../../utils/designationFilters";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CommonLoader from "../../common_components/CommonLoader";
import CustomMonthComparePicker from "../../common_components/daterangepicker/CustomMonthComparePicker";
import { toast } from "../../common_components/Toast/ToastUtils";
import BillingSummaryCards from "./components/BillingSummaryCards";
import BillingSummaryFilters from "./components/BillingSummaryFilters";
import BillingSummaryTable from "./components/BillingSummaryTable";
import { EMPTY_FILTERS, INVOICE_MISMATCH_FILTER } from "./billingSummary.constants";
import { buildSummaryCards } from "./billingSummary.cards";
import { mapBillingSummaryRows, mapBillingSummaryTotals, mapInvoiceMappingEntries, } from "./billingSummary.mappers";
const MODULE_KEY = "billing_summary";
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
/** year_month ("YYYY-MM") from a CalendarDate — the API's required parameter. */
const toYearMonth = (d) => d ? `${d.year}-${String(d.month).padStart(2, "0")}` : "";
/** The applied month, stored as "YYYY-MM" so the selection survives a reload. */
const MONTH_STORAGE_KEY = "billing_summary_month";
/**
 * The page opens on the PREVIOUS month: a month's invoices go out during the month
 * that follows it, so in August the run being worked on measures July. Same reason
 * `billingHeading` names the month after the selection.
 */
const previousMonth = () => startOfMonth(today(getLocalTimeZone())).subtract({ months: 1 });
/**
 * The month the user last applied, falling back to `previousMonth()` when nothing is
 * stored, the value is unreadable (private mode) or it isn't a valid "YYYY-MM".
 */
/**
 * How often to ask whether the invoice refresh has finished, and how long to keep asking.
 *
 * The server re-reads the queued invoices five at a time, batch after batch, at roughly
 * 1.5–6s a batch. 500 invoices is 100 batches — up to ~10 minutes — so the cap is 20, not
 * the 10 the old one-batch-a-minute worker was sized for. 80 × 15s ≈ 20 minutes.
 *
 * Hitting the cap is not a failure: it stops the poll and says the job is still going rather
 * than spinning forever, and the listing is re-read either way so partial results show.
 */
const REFRESH_POLL_MS = 15000;
const REFRESH_POLL_MAX_ATTEMPTS = 80;
/**
 * The same for a generation, asked more often. One invoice — the common case — is raised in
 * a couple of seconds, and a 15s poll would leave the button spinning long after the work
 * was done. A whole month is minutes, so the window is still wide: 180 × 5s ≈ 15 minutes.
 */
const GENERATE_POLL_MS = 5000;
const GENERATE_POLL_MAX_ATTEMPTS = 180;
const readStoredMonth = () => {
    try {
        const raw = localStorage.getItem(MONTH_STORAGE_KEY);
        const parsed = raw && /^(\d{4})-(\d{2})$/.exec(raw);
        if (!parsed)
            return previousMonth();
        const year = Number(parsed[1]);
        const month = Number(parsed[2]);
        if (month < 1 || month > 12)
            return previousMonth();
        return new CalendarDate(year, month, 1);
    }
    catch {
        return previousMonth();
    }
};
/**
 * Clients are invoiced in the month AFTER the one being measured, so the heading
 * names the billing month — the month following the selection (e.g. picking
 * Jul 2026 reads "August 2026 Billing"), matching the legacy report.
 */
const billingHeading = (month) => {
    if (!month)
        return "Billing";
    const next = month.add({ months: 1 });
    return `${MONTHS[next.month - 1]} ${next.year} Billing`;
};
/**
 * Billing Summary — month-scoped billing KPIs, filter bar and client listing.
 *
 * Everything on the page comes from `GET /api/billing-summary`: the endpoint returns
 * the already-filtered rows plus the KPI card figures, which it aggregates over the
 * whole billing cycle so they don't move as the listing is filtered. The table's
 * footer totals are the opposite — summed from the visible rows.
 */
export default function BillingSummary() {
    const dispatch = useDispatch();
    const hasFullAccess = useSelector(selectBillingHasFullAccess);
    // Month drives the whole page; comparison is intentionally not offered here.
    // Passed as an initialiser so storage is read once on mount, not every render.
    const [month, setMonth] = useState(readStoredMonth);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Remember the applied month for the next visit. Never removes the key — a null
    // month only happens if the picker is cleared, and the stored choice is still the
    // better thing to come back to than resetting to the default.
    useEffect(() => {
        try {
            if (month)
                localStorage.setItem(MONTH_STORAGE_KEY, toYearMonth(month));
        }
        catch {
            /* ignore storage errors (e.g. private mode) */
        }
    }, [month]);
    const summary = useSelector(selectBillingSummary);
    const loading = useSelector(selectBillingSummaryLoading);
    const error = useSelector(selectBillingSummaryError);
    const savingDevelopmentHours = useSelector(selectSavingDevelopmentHours);
    const savingCommissionAmount = useSelector(selectSavingCommissionAmount);
    const savingAdjustedAmounts = useSelector(selectSavingAdjustedAmounts);
    const savingInvoiceMappings = useSelector(selectSavingInvoiceMappings);
    const generatingInvoice = useSelector(selectGeneratingInvoice);
    /**
     * Refresh in progress. Local rather than in the slice because ONE flag has to span
     * both phases — starting the job and polling it to completion — and only this page
     * cares.
     */
    const [refreshing, setRefreshing] = useState(false);
    /**
     * Generation in progress, for the same reason: the thunk's own flag drops as soon as the
     * clients are queued, but the pushing carries on for minutes afterwards, so the Generate
     * button has to stay disabled on THIS flag until the polling below says it's done.
     */
    const [generating, setGenerating] = useState(false);
    /** Export in flight — drives the toolbar's Export button. */
    const [exporting, setExporting] = useState(false);
    const token = useSelector(selectToken);
    const heading = useMemo(() => billingHeading(month), [month]);
    // ── Fetch on mount + whenever the month or ANY filter changes (deduped, same as
    // the Commission Tracker). Every filter is a query param — the endpoint does all
    // the filtering, so the response is rendered as it arrives.
    const lastRequestKey = useRef(null);
    const loadSummary = useCallback(() => {
        const yearMonth = toYearMonth(month);
        if (!yearMonth)
            return;
        const payload = {
            year_month: yearMonth,
            type: (filters.type || "all"),
            ...(filters.am_id ? { am_id: filters.am_id } : {}),
            ...(filters.source ? { source: filters.source } : {}),
            ...(filters.invoice_status ? { invoice_status: filters.invoice_status } : {}),
            ...(filters.diff_amount ? { diff_amount: filters.diff_amount } : {}),
            ...(filters.sync_status ? { sync_status: filters.sync_status } : {}),
            ...(filters.excluded_client ? { excluded_client: filters.excluded_client } : {}),
        };
        const key = JSON.stringify(payload);
        if (lastRequestKey.current === key)
            return;
        lastRequestKey.current = key;
        dispatch(fetchBillingSummary(payload));
    }, [dispatch, month, filters]);
    useEffect(() => {
        loadSummary();
    }, [loadSummary]);
    // ── View models ───────────────────────────────────────────
    const rows = useMemo(() => mapBillingSummaryRows(summary?.summary), [summary]);
    // Totals are summed from the rows the API returned, so they track the filters.
    const totals = useMemo(() => mapBillingSummaryTotals(summary?.summary), [summary]);
    // The cards do NOT track the filters: the API aggregates them over the whole
    // billing cycle for the month, so they hold still while the listing narrows.
    const cards = useMemo(() => buildSummaryCards(summary?.cards ?? EMPTY_BILLING_CARDS), [summary]);
    // ── Account Manager roster (same source as Commission Tracker / Account
    // Performance), so the AM filter sends real user ids to the API.
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    useEffect(() => {
        dispatch(fetchDesignationWiseUsers());
    }, [dispatch]);
    const amOptions = useMemo(() => {
        const managers = collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS);
        return managers.map((u) => ({ value: u._id, label: `${u.first_name} ${u.last_name}` }));
    }, [designationWiseUsers]);
    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);
    const refreshPollRef = useRef(null);
    /**
     * The poll's callback outlives the render that started it, so it reads the CURRENT
     * loader — the user may have changed month or filters while the worker was running,
     * and the reload has to honour that rather than the state captured at kick-off.
     */
    const loadSummaryRef = useRef(loadSummary);
    useEffect(() => {
        loadSummaryRef.current = loadSummary;
    }, [loadSummary]);
    const stopRefreshPolling = useCallback(() => {
        if (refreshPollRef.current !== null) {
            clearInterval(refreshPollRef.current);
            refreshPollRef.current = null;
        }
    }, []);
    // Navigating away mid-refresh must not leave the interval firing.
    useEffect(() => stopRefreshPolling, [stopRefreshPolling]);
    const generatePollRef = useRef(null);
    const stopGenerationPolling = useCallback(() => {
        if (generatePollRef.current !== null) {
            clearInterval(generatePollRef.current);
            generatePollRef.current = null;
        }
    }, []);
    // Same as above — an interval must not outlive the page.
    useEffect(() => stopGenerationPolling, [stopGenerationPolling]);
    /**
     * Wait for a generation to finish, then re-read the listing.
     *
     * Kept separate from the refresh poll rather than shared with it: this one owns the
     * Generate button's loader and reports on invoices being raised, and the two can be in
     * flight at once (a refresh started before a generation is still draining its own queue).
     *
     * `/refresh-status` reports ONE flag for both kinds of background work, so this waits for
     * whichever is running — which is what the button should do anyway, since neither can
     * start while the other holds the QuickBooks connection.
     */
    const startGenerationPolling = useCallback(() => {
        stopGenerationPolling();
        let attempts = 0;
        const finish = (stillRunning) => {
            stopGenerationPolling();
            setGenerating(false);
            if (stillRunning) {
                toast.info("Invoices are still being generated in the background. Reload in a few minutes to see the rest.");
            }
            // Each client's verdict is its Sync Status on the row, so the listing is the
            // report — there is no per-client outcome in the response any more.
            lastRequestKey.current = null;
            loadSummaryRef.current();
        };
        generatePollRef.current = setInterval(async () => {
            attempts += 1;
            try {
                const status = await dispatch(fetchRefreshStatus()).unwrap();
                if (!status.running)
                    finish(false);
                else if (attempts >= GENERATE_POLL_MAX_ATTEMPTS)
                    finish(true);
            }
            catch {
                stopGenerationPolling();
                setGenerating(false);
                toast.error("Lost track of the invoice generation. Reload the page to see where it got to.");
            }
        }, GENERATE_POLL_MS);
    }, [dispatch, stopGenerationPolling]);
    const startRefreshPolling = useCallback(() => {
        stopRefreshPolling();
        let attempts = 0;
        // Shared exit: stop polling, re-read the listing, and say so when the worker is
        // still going. A capped-out poll has still landed partial results, so it reloads.
        const finish = (stillRunning) => {
            stopRefreshPolling();
            setRefreshing(false);
            if (stillRunning) {
                toast.info("The invoice refresh is still running in the background. Refresh again in a few minutes to see the rest.");
            }
            lastRequestKey.current = null;
            loadSummaryRef.current();
        };
        refreshPollRef.current = setInterval(async () => {
            attempts += 1;
            try {
                const status = await dispatch(fetchRefreshStatus()).unwrap();
                if (!status.running)
                    finish(false);
                else if (attempts >= REFRESH_POLL_MAX_ATTEMPTS)
                    finish(true);
            }
            catch {
                stopRefreshPolling();
                setRefreshing(false);
                toast.error("Lost track of the invoice refresh. Reload the page to see where it got to.");
            }
        }, REFRESH_POLL_MS);
    }, [dispatch, stopRefreshPolling]);
    /**
     * Adopt a refresh that was ALREADY running when the page opened.
     *
     * The queue lives on the server and is global, so one started in another tab, by another
     * admin, or by the weekly seeder is still draining when this page loads — and until the
     * poll below takes over, the button here looks pressable while the endpoint would refuse
     * it. Asked once on mount; the poll drops the flag when the queue empties and re-reads
     * the listing, so this page also picks up whatever that refresh changed.
     *
     * Only `refreshing` counts, not `running`: a generation in flight is the Generate
     * button's business and says nothing about the refresh queue.
     */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const status = await dispatch(fetchRefreshStatus()).unwrap();
                // A poll already ticking means the button got there first — leave it alone
                // rather than restarting its attempt count.
                if (cancelled || !status.refreshing || refreshPollRef.current !== null)
                    return;
                setRefreshing(true);
                startRefreshPolling();
            }
            catch {
                /* Left pressable on a failed check — the endpoint refuses a second refresh anyway. */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dispatch, startRefreshPolling]);
    /**
     * Reconcile the month against QuickBooks, then wait for the background worker.
     *
     * The endpoint only queues the work, so the icon keeps spinning through the polling
     * that follows.
     */
    const handleRefresh = useCallback(async () => {
        const yearMonth = toYearMonth(month);
        if (!yearMonth || refreshing)
            return;
        setRefreshing(true);
        stopRefreshPolling();
        try {
            const kickoff = await dispatch(refreshInvoices({ year_month: yearMonth })).unwrap();
            // A client with two QuickBooks invoices for the month keeps the one already
            // recorded. Surfaced because the other is otherwise invisible here.
            // if (kickoff.multiple.length) {
            //     toast.warning(`More than one invoice found in QuickBooks for: ${kickoff.multiple.join(", ")}. The recorded invoice was kept.`);
            // }
            // `started: false` is benign — either a refresh was already in flight (whose
            // completion is still worth waiting for) or the month had nothing to refresh.
            // The status check below tells those apart, so the message is informational.
            if (kickoff.started) {
                // The count sets the expectation. A few hundred invoices take minutes to work
                // through, and a spinner sitting there that long with no explanation reads as
                // a hang rather than as progress.
                toast.success(kickoff.queued
                    ? `${kickoff.message || "Invoice refresh started."} this can take a few minutes.`
                    : kickoff.message || "Invoice refresh started.");
            }
            else if (kickoff.message) {
                toast.info(kickoff.message);
            }
            // Asked immediately: a month with nothing queued is already finished, so
            // there's no reason to wait a full interval before reloading.
            const { running } = await dispatch(fetchRefreshStatus()).unwrap();
            if (!running) {
                setRefreshing(false);
                lastRequestKey.current = null;
                loadSummaryRef.current();
                return;
            }
            startRefreshPolling();
        }
        catch (err) {
            setRefreshing(false);
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to start the invoice refresh. Please try again.");
        }
    }, [dispatch, month, refreshing, startRefreshPolling, stopRefreshPolling]);
    /**
     * Download the listing as CSV.
     *
     * Sent with the SAME month + filters the listing was fetched with, so the file matches
     * what's on screen — the endpoint re-reads through the same service rather than being
     * handed the rendered rows.
     *
     * Not a thunk: the response is a file, so there's no slice state to hold. Same shape as
     * the LTV Segment Report's export.
     */
    const handleExport = useCallback(async () => {
        const yearMonth = toYearMonth(month);
        if (!yearMonth || exporting)
            return;
        setExporting(true);
        try {
            const response = await ApiCall("POST", "/api/billing-summary/export", {
                year_month: yearMonth,
                type: (filters.type || "all"),
                ...(filters.am_id ? { am_id: filters.am_id } : {}),
                ...(filters.source ? { source: filters.source } : {}),
                ...(filters.invoice_status ? { invoice_status: filters.invoice_status } : {}),
                ...(filters.diff_amount ? { diff_amount: filters.diff_amount } : {}),
                ...(filters.sync_status ? { sync_status: filters.sync_status } : {}),
                ...(filters.excluded_client ? { excluded_client: filters.excluded_client } : {}),
            }, { Authorization: token || "", "x-module-key": MODULE_KEY }, 7 * 60 * 1000, "blob");
            if (!response || response.status !== 200 || !response.data) {
                throw new Error("Export failed");
            }
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `billing_summary_${yearMonth}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            console.error("Billing Summary export error:", err);
            toast.error("Failed to download the CSV. Please try again.");
        }
        finally {
            setExporting(false);
        }
    }, [month, filters, token, exporting]);
    /**
     * Raise (or revise) invoices for the checked clients.
     *
     * A client already holding an invoice for the month is revised in place rather than
     * invoiced twice, so re-running after a correction is safe.
     */
    const handleGenerateInvoice = useCallback(async (rowIds, developmentHours) => {
        const yearMonth = toYearMonth(month);
        if (!rowIds.length || !yearMonth)
            return;
        // Hours entered in the repeater have to be PERSISTED before invoicing: the endpoint
        // recomputes every figure from what's stored and takes no amounts from the request,
        // so skipping this would omit them from the very invoice they were added for. One
        // client only, which is all the repeater offers.
        //
        // Handled separately from the invoicing below so a failure here returns false with
        // NOTHING re-fetched — that keeps the repeater open with the typed lines intact,
        // where a reload would unmount it and lose them.
        if (developmentHours?.length && rowIds.length === 1) {
            try {
                await dispatch(saveDevelopmentHours({
                    client_id: rowIds[0],
                    year_month: yearMonth,
                    development_hours: developmentHours.map((entry) => ({
                        type: entry.type,
                        description: entry.description,
                        hours: entry.hours,
                        rate: entry.rate,
                    })),
                })).unwrap();
            }
            catch (err) {
                toast.error(typeof err === "string"
                    ? err
                    : err?.message || "Failed to save the development hours, so no invoice was generated.");
                return false;
            }
        }
        try {
            const kickoff = await dispatch(generateInvoice({ year_month: yearMonth, client_ids: rowIds })).unwrap();
            // `started: false` means a generation was already in flight. Benign, and its
            // completion is still worth waiting for, so the polling below runs either way.
            if (kickoff.started) {
                toast.success(kickoff.message || "Generating invoice(s) this can take a few minutes.");
            }
            else if (kickoff.message) {
                toast.info(kickoff.message);
            }
            setGenerating(true);
            startGenerationPolling();
            // True as soon as the work is under way, so the repeater closes and the selection
            // clears instead of sitting open behind a spinner for the whole run.
            return true;
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to start generating the invoice(s). Please try again.");
            return false;
        }
    }, [dispatch, month, startGenerationPolling]);
    /**
     * Persist one client's development hours for the selected month.
     *
     * The repeater posts its COMPLETE state, so the endpoint replaces the stored array
     * — removing a line in the UI removes it in the DB. Lines the user never filled in
     * are dropped server-side, so they're sent as-is rather than pre-filtered here.
     *
     * Returns false on failure, which keeps the modal open with the typed lines.
     */
    const handleSaveDevelopmentHours = useCallback(async (rowId, entries) => {
        const yearMonth = toYearMonth(month);
        if (!rowId || !yearMonth)
            return false;
        try {
            await dispatch(saveDevelopmentHours({
                client_id: rowId,
                year_month: yearMonth,
                development_hours: entries.map((entry) => ({
                    type: entry.type,
                    description: entry.description,
                    hours: entry.hours,
                    rate: entry.rate,
                })),
            })).unwrap();
            toast.success("Development hours saved.");
            // Re-read the listing rather than patching the row: Development Hours feeds
            // Total Bill, the footer totals and the KPI cards, so they all have to move
            // together. Dropping the dedup key lets the same month/filters re-request.
            lastRequestKey.current = null;
            loadSummary();
            return true;
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to save the development hours. Please try again.");
            return false;
        }
    }, [dispatch, month, loadSummary]);
    /**
     * Persist the commission typed into the "Invoice Details" modal for one client.
     *
     * The row is Tab-sourced, so its commission isn't measured through a feed — the saved
     * amount REPLACES the row's Commissions and the Total Bill built from it, which is why
     * the listing is re-read rather than the row patched.
     *
     * Returns false on failure, which keeps the modal open with what was typed.
     */
    const handleSaveInvoiceDetails = useCallback(async (rowId, totalCommissions, invoiceSalesDescription) => {
        const yearMonth = toYearMonth(month);
        if (!rowId || !yearMonth)
            return false;
        try {
            await dispatch(saveCommissionAmount({
                client_id: rowId,
                year_month: yearMonth,
                commission_amount: totalCommissions,
                sales_description: invoiceSalesDescription,
            })).unwrap();
            toast.success("Commission details saved.");
            // Same reason as the development hours above: the amount feeds Commissions,
            // Total Bill, the footer totals and the KPI cards, so they all have to move
            // together. Dropping the dedup key lets the same month/filters re-request.
            lastRequestKey.current = null;
            loadSummary();
            return true;
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to save the commission details. Please try again.");
            return false;
        }
    }, [dispatch, month, loadSummary]);
    /**
     * Persist hand-entered Total Bill overrides for the checked clients.
     *
     * A blank field CLEARS that client's override rather than billing zero, so it's sent
     * as `null` — the endpoint draws exactly that distinction, and `0` remains a real
     * instruction to bill nothing.
     *
     * Returns false on failure, which keeps the modal open with the typed amounts.
     */
    const handleSaveAdjustedAmounts = useCallback(async (entries) => {
        const yearMonth = toYearMonth(month);
        if (!entries.length || !yearMonth)
            return false;
        try {
            await dispatch(saveAdjustedAmounts({
                year_month: yearMonth,
                invoices: entries.map((entry) => ({
                    client_id: entry.id,
                    adjusted_amount: entry.totalBill.trim() === "" ? null : Number(entry.totalBill),
                })),
            })).unwrap();
            toast.success("Total Bill updated.");
            // Re-read the listing rather than patching rows: an override feeds Total Bill,
            // the footer totals and the KPI cards, so they all have to move together.
            lastRequestKey.current = null;
            loadSummary();
            return true;
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to update the Total Bill. Please try again.");
            return false;
        }
    }, [dispatch, month, loadSummary]);
    /**
     * The ADDITIONAL QuickBooks invoices the checked clients hold for the billing month.
     *
     * Read as the Mapping Invoice modal opens, and re-read on every open: the list comes from
     * QuickBooks, so a cached one could offer an invoice that has since been deleted there.
     * Rejections are re-thrown for the modal to show — it sits above the toast layer.
     */
    const handleLoadInvoiceMappings = useCallback(async (clientIds) => {
        const yearMonth = toYearMonth(month);
        if (!clientIds.length || !yearMonth)
            return [];
        const invoices = await dispatch(fetchMappableInvoices({ year_month: yearMonth, client_ids: clientIds })).unwrap();
        return mapInvoiceMappingEntries(invoices);
    }, [dispatch, month]);
    /**
     * Persist which extra invoices are mapped onto each checked client's month.
     *
     * The modal hands back EVERY invoice it listed with its current tick, and the endpoint
     * replaces each client's stored mapping — so this is what both adds a mapping and removes
     * one, and a client with nothing ticked has to be sent with an empty list rather than
     * omitted. Grouping over the whole list is what guarantees that.
     *
     * Returns false on failure, which keeps the modal open with the ticks intact.
     */
    const handleSaveInvoiceMappings = useCallback(async (entries) => {
        const yearMonth = toYearMonth(month);
        if (!yearMonth)
            return false;
        const byClient = new Map();
        for (const entry of entries) {
            const selected = byClient.get(entry.clientId) || [];
            if (entry.selected)
                selected.push(entry.invoiceId);
            byClient.set(entry.clientId, selected);
        }
        if (!byClient.size)
            return false;
        try {
            const result = await dispatch(saveInvoiceMappings({
                year_month: yearMonth,
                mappings: [...byClient].map(([client_id, invoice_ids]) => ({ client_id, invoice_ids })),
            })).unwrap();
            // Named rather than swallowed: those invoices are NOT mapped, and the modal will
            // show them unticked when it's next opened.
            if (result.failed.length) {
                toast.warning(`${result.failed.length} invoice(s) couldn't be read from QuickBooks and weren't mapped.`);
            }
            else {
                toast.success("Invoice mapping saved.");
            }
            // Re-read the listing rather than patching rows: a mapped invoice's amount is
            // summed into Invoice Amount, which feeds the footer totals and the KPI cards, and
            // each mapping also adds a PDF icon to the Client cell — so they all have to move
            // together. Dropping the dedup key lets the same month/filters re-request.
            lastRequestKey.current = null;
            loadSummary();
            return true;
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to save the invoice mapping. Please try again.");
            return false;
        }
    }, [dispatch, month, loadSummary]);
    // ✅ Loading State
    if (loading || exporting) {
        return (_jsxs(Box, { sx: {
                position: "absolute",
                inset: 0,
                background: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                zIndex: 100,
            }, children: [_jsx(CommonLoader, { className: "table-loader" }), exporting && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: "Preparing your export. This may take a moment. It will download automatically once it's ready\"" }))] }));
    }
    return (_jsx(PageContainer, { children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "8px", position: "relative", minHeight: 300 }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: 
                    // When the month's invoices last finished reconciling against
                    // QuickBooks. Null until a refresh has completed for the month,
                    // which reads as "Last sync date not available."
                    _jsx(LastUpdatedInfo, { updatedAt: summary?.last_updated ?? null, onRefresh: handleRefresh, 
                        // Keeps the icon spinning across BOTH phases — queuing the job
                        // and polling the worker — since only the second one takes time.
                        loading: loading || refreshing, moduleName: MODULE_KEY, disabledReason: refreshing ? "An invoice refresh is already running." : undefined }), rightSection: _jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "flex-end" }, children: _jsx(CustomMonthComparePicker, { value: month, onApply: ({ main }) => setMonth(main), placeholder: "Select Month", 
                            // Billing has no month-over-month view — the Compare
                            // toggle and the "Vs" box stay hidden.
                            comparison: false, futureMonths: 10 }) }) }), _jsx(BillingSummaryCards, { cards: cards, heading: heading }), _jsx(BillingSummaryFilters, { filters: filters, onChange: handleFilterChange, amOptions: amOptions }), loading ? (_jsx(Box, { sx: { position: "absolute", inset: 0, top: 140, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : error ? (_jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center" }, children: error })) : (_jsx(BillingSummaryTable, { yearMonth: toYearMonth(month), rows: rows, totals: totals, hasFullAccess: hasFullAccess, onExport: handleExport, exporting: exporting, onGenerateInvoice: handleGenerateInvoice, 
                    // Both flags: the thunk's own covers the kick-off request, `generating`
                    // covers the minutes of pushing that follow it.
                    generatingInvoice: generatingInvoice || generating, onSaveDevelopmentHours: handleSaveDevelopmentHours, savingDevelopmentHours: savingDevelopmentHours, 
                    // The link icon on a Tab-sourced row opens this instead of drilling
                    // through to the Commission Tracker — a Tab commission is entered
                    // here, since no feed reports one.
                    onSaveInvoiceDetails: handleSaveInvoiceDetails, savingInvoiceDetails: savingCommissionAmount, 
                    // "Update Total Bill" is offered only against the mismatched rows —
                    // reconciling a bill is what that filter is for.
                    canAdjustTotalBill: filters.diff_amount === INVOICE_MISMATCH_FILTER, onSaveAdjustedAmounts: handleSaveAdjustedAmounts, savingAdjustedAmounts: savingAdjustedAmounts, 
                    // "Mapping Invoice" needs no filter: it's offered per checked row,
                    // gated on the `has_multiple_invoice` flag the refresh maintains.
                    onLoadInvoiceMappings: handleLoadInvoiceMappings, onSaveInvoiceMappings: handleSaveInvoiceMappings, savingInvoiceMappings: savingInvoiceMappings }))] }) }));
}
